import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import Sidebar from './Sidebar';
import { LogoFull } from './Logo';
import { Bell, HelpCircle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import HelpDot from './HelpDot';
import NotificationsBell from './NotificationsBell';
import Copilot from './Copilot';

const TITLES = {
  '/app/dashboard': { title: 'Dashboard', subtitle: 'Estado general de cumplimiento y próximas acciones.' },
  '/app/diagnostico': { title: 'Diagnóstico de cumplimiento', subtitle: 'Preguntas guiadas para calcular su score inicial.', helpKey: 'diagnostico' },
  '/app/plan': { title: 'Plan de acción', subtitle: 'Tareas priorizadas generadas por su último diagnóstico.' },
  '/app/rat': { title: 'Registro de tratamientos (RAT)', subtitle: 'Inventario de actividades de tratamiento exigido por la Ley 21.719.', helpKey: 'rat' },
  '/app/sistemas': { title: 'Catastro de sistemas', subtitle: 'Plataformas, procesos y proveedores que tratan datos personales.', helpKey: 'catastro_sistemas' },
  '/app/documentos': { title: 'Documentos legales', subtitle: 'Política de privacidad, cookies, DPAs y avisos ARCO+.', helpKey: 'documentos_legales' },
  '/app/cookies': { title: 'Cookies (CMP)', subtitle: 'Configura el banner y revisa los consentimientos con evidencia auditable.', helpKey: 'cookies' },
  '/app/arco': { title: 'Solicitudes ARCO+', subtitle: 'Bandeja de solicitudes públicas con SLA de 15 días hábiles.', helpKey: 'arco' },
  '/app/brechas': { title: 'Brechas de seguridad · 72h', subtitle: 'Reportar, contener, notificar a la Agencia y comunicar a titulares afectados.', helpKey: 'brecha' },
  '/app/dpia': { title: 'DPIA · Evaluación de Impacto', subtitle: 'Evaluación obligatoria del art. 15 bis para tratamientos con alto riesgo.', helpKey: 'dpia' },
  '/app/fiscalizacion': { title: 'Carpeta de fiscalización', subtitle: 'Genere y descargue el paquete completo de evidencia para la Agencia.' },
  '/app/trazabilidad': { title: 'Registro de accesos y trazabilidad', subtitle: 'Bitácora interna + eventos manuales + importación desde otros sistemas.', helpKey: 'trazabilidad' },
  '/app/auditoria': { title: 'Bitácora de auditoría', subtitle: 'Registro inmutable para fiscalizaciones de la Agencia.' },
  '/app/equipo': { title: 'Equipo', subtitle: 'Miembros y roles de su organización.' },
  '/app/configuracion': { title: 'Configuración', subtitle: 'Datos de la organización y de su cuenta.' },
};

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const header = useMemo(() => {
    const p = location.pathname;
    // Detalle de brecha: página propia con header custom → ocultamos el shell header
    if (p.startsWith('/app/brechas/') && p !== '/app/brechas') {
      return { title: '', subtitle: '', hide: true };
    }
    if (p.startsWith('/app/dpia/') && p !== '/app/dpia') {
      return { title: '', subtitle: '', hide: false };
    }
    return TITLES[p] || { title: '', subtitle: '' };
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#F7F8FA]" data-testid="app-shell">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Watermark auditor externo */}
        {user?.role === 'auditor_externo' && (
          <div className="bg-[#FEF3C7] border-b-2 border-[#F59E0B] px-6 lg:px-10 py-2 flex items-center gap-2" data-testid="auditor-watermark">
            <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#92400E]">Sesión de auditoría externa · solo lectura</span>
            <span className="text-[11px] text-[#92400E] ml-auto">
              {user.access_expires_at ? `Vence ${new Date(user.access_expires_at).toLocaleDateString('es-CL')}` : 'Sin caducidad'}
            </span>
          </div>
        )}
        {/* Mobile header logo */}
        <div className="lg:hidden bg-[#0B1220] px-4 py-3 flex items-center justify-between border-b border-white/5">
          <button onClick={() => navigate('/app/dashboard')} data-testid="mobile-home">
            <LogoFull height={26} />
          </button>
          <span className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-white/70 uppercase tracking-wider">
            {organization?.razon_social?.slice(0, 24) || '—'}
          </span>
        </div>

        {/* Content header */}
        {!header.hide && (
        <header className="bg-white border-b border-[#E5E7EB] px-6 lg:px-10 py-7">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-[26px] md:text-[32px] font-semibold text-[#1C2329] tracking-[-0.025em] leading-[1.1] flex items-center gap-2.5 flex-wrap" data-testid="page-title">
                {header.title}
                {header.helpKey && <HelpDot termKey={header.helpKey} size="lg" />}
              </h1>
              {header.subtitle && (
                <p className="text-sm text-[#6B7280] mt-1.5 max-w-2xl">{header.subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button className="pv-btn-secondary flex items-center gap-2" data-testid="header-help">
                <HelpCircle size={14} strokeWidth={1.5} />
                <span>Centro de ayuda</span>
              </button>
              <NotificationsBell />
            </div>
          </div>
        </header>
        )}

        <main className="flex-1 px-6 lg:px-10 py-8">
          <Outlet />
        </main>

        <footer className="px-6 lg:px-10 py-4 text-[12px] text-[#6B7280] border-t border-[#E5E7EB] bg-white flex flex-col md:flex-row md:justify-between gap-2">
          <div>© {new Date().getFullYear()} Priviax · Cumplimiento Ley N.º 21.719 de Chile.</div>
          <div className="tabular-nums">v1.0.0</div>
        </footer>
      </div>
      <Copilot />
    </div>
  );
}
